import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice Beignets - Calculez Calories et Graisses des Beignets";
const DESCRIPTION =
  "Calculatrice Beignets - Calculez Calories et Graisses des Beignets pour adapter vos recettes, portions et conversions en cuisine. Resultat rapide et pratique pour cuisiner avec plus de precision chaque jour.";
const KEYWORDS =
  "beignets calories et graisses des beignets, calculatrice beignets calories et graisses des beignets, calcul en ligne, cuisine, recette, conversion, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/donut/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice Beignets",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice Beignets",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Combien de calories dans un beignet glacé ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Un beignet glacé de taille moyenne contient environ 190-240 calories, avec environ 10-12 g de matières grasses et 22-30 g de glucides. Le nombre varie selon la taille et la marque.",
        },
      },
      {
        "@type": "Question",
        name: "Le beignet fourré à la crème a-t-il plus de calories qu'un beignet ordinaire ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Oui, les beignets fourrés à la crème ou la gelée ont généralement 300-400 calories, soit beaucoup plus qu'un beignet ordinaire. Le remplissage ajoute des graisses et des sucres supplémentaires.",
        },
      },
      {
        "@type": "Question",
        name: "Quel type de beignet a le moins de calories ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Les beignets simples ou saupoudrés de sucre sont les plus légers, entre 170-220 calories. Les beignets trous (mini beignets) sont encore plus légers avec seulement 60-100 calories.",
        },
      },
    ],
  },
];

// Donut types with nutritional data per 100g
const DONUT_TYPES = {
  glazed: {
    name: "Beignet Glacé",
    calories: 380,
    fat: 17,
    saturatedFat: 7,
    carbs: 52,
    sugar: 22,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 60,
  },
  chocolate_glazed: {
    name: "Beignet Glacé Chocolat",
    calories: 400,
    fat: 20,
    saturatedFat: 9,
    carbs: 52,
    sugar: 28,
    protein: 5,
    sodium: 290,
    fiber: 1.5,
    weightG: 75,
  },
  plain: {
    name: "Beignet Simple",
    calories: 350,
    fat: 15,
    saturatedFat: 6,
    carbs: 48,
    sugar: 12,
    protein: 5,
    sodium: 260,
    fiber: 1,
    weightG: 55,
  },
  sugar: {
    name: "Beignet Sucré",
    calories: 390,
    fat: 17,
    saturatedFat: 7,
    carbs: 55,
    sugar: 20,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 60,
  },
  powdered_sugar: {
    name: "Beignet Sucre Poudre",
    calories: 395,
    fat: 18,
    saturatedFat: 7.5,
    carbs: 54,
    sugar: 21,
    protein: 5,
    sodium: 280,
    fiber: 1,
    weightG: 65,
  },
  cream_filled: {
    name: "Beignet Fourré Crème",
    calories: 420,
    fat: 22,
    saturatedFat: 10,
    carbs: 52,
    sugar: 25,
    protein: 5,
    sodium: 300,
    fiber: 0.5,
    weightG: 85,
  },
  jelly: {
    name: "Beignet Gelée",
    calories: 410,
    fat: 18,
    saturatedFat: 7,
    carbs: 58,
    sugar: 30,
    protein: 5,
    sodium: 290,
    fiber: 0.5,
    weightG: 85,
  },
  frosted: {
    name: "Beignet Glaçage",
    calories: 430,
    fat: 21,
    saturatedFat: 9,
    carbs: 58,
    sugar: 32,
    protein: 5,
    sodium: 300,
    fiber: 0.5,
    weightG: 75,
  },
  old_fashioned: {
    name: "Beignet Classique",
    calories: 450,
    fat: 22,
    saturatedFat: 10,
    carbs: 58,
    sugar: 22,
    protein: 6,
    sodium: 310,
    fiber: 1,
    weightG: 80,
  },
  donut_hole: {
    name: "Mini Beignet (Beignet Trou)",
    calories: 370,
    fat: 16,
    saturatedFat: 6,
    carbs: 51,
    sugar: 18,
    protein: 5,
    sodium: 270,
    fiber: 1,
    weightG: 25,
  },
};

const SIZE_MULTIPLIERS = {
  mini: { name: "Très Petit (mini)", multiplier: 0.5 },
  small: { name: "Petit", multiplier: 0.75 },
  medium: { name: "Moyen", multiplier: 1.0 },
  large: { name: "Grand", multiplier: 1.35 },
  extra_large: { name: "Très Grand", multiplier: 1.7 },
};

function DonutCalculator() {
  const [donutType, setDonutType] = useState("glazed");
  const [size, setSize] = useState("medium");
  const [quantity, setQuantity] = useState(1);
  const [customWeight, setCustomWeight] = useState("");
  const [useCustomWeight, setUseCustomWeight] = useState(false);
  const [dailyCalories, setDailyCalories] = useState(2000);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateResult = () => {
    try {
      setError("");
      const donut = DONUT_TYPES[donutType];
      const sizeMultiplier = SIZE_MULTIPLIERS[size].multiplier;

      let weightGrams;
      if (useCustomWeight && customWeight) {
        weightGrams = parseFloat(customWeight);
        if (isNaN(weightGrams) || weightGrams <= 0) {
          setError("Veuillez entrer un poids correct supérieur à zéro");
          setResult(null);
          return;
        }
      } else {
        weightGrams = donut.weightG * sizeMultiplier;
      }

      const qty = parseInt(quantity) || 1;
      const factor = weightGrams / 100;

      const caloriesPerPiece = donut.calories * factor;
      const fatPerPiece = donut.fat * factor;
      const saturatedFatPerPiece = donut.saturatedFat * factor;
      const carbsPerPiece = donut.carbs * factor;
      const sugarPerPiece = donut.sugar * factor;
      const proteinPerPiece = donut.protein * factor;
      const sodiumPerPiece = donut.sodium * factor;
      const fiberPerPiece = donut.fiber * factor;

      const totalCalories = caloriesPerPiece * qty;
      const totalFat = fatPerPiece * qty;
      const totalSaturatedFat = saturatedFatPerPiece * qty;
      const totalCarbs = carbsPerPiece * qty;
      const totalSugar = sugarPerPiece * qty;
      const totalProtein = proteinPerPiece * qty;
      const totalSodium = sodiumPerPiece * qty;
      const totalFiber = fiberPerPiece * qty;

      const dailyCaloriesPercent = (totalCalories / dailyCalories) * 100;
      const dailyFatPercent = (totalFat / 78) * 100;
      const dailySugarPercent = (totalSugar / 50) * 100;

      const fatCalories = totalFat * 9;
      const carbCalories = totalCarbs * 4;
      const proteinCalories = totalProtein * 4;
      const totalMacroCalories = fatCalories + carbCalories + proteinCalories;

      const fatPercent = (fatCalories / totalMacroCalories) * 100;
      const carbPercent = (carbCalories / totalMacroCalories) * 100;
      const proteinPercent = (proteinCalories / totalMacroCalories) * 100;

      setResult({
        weightGrams,
        qty,
        donutName: donut.name,
        sizeName: SIZE_MULTIPLIERS[size].name,
        caloriesPerPiece,
        totalCalories,
        totalFat,
        totalSaturatedFat,
        totalCarbs,
        totalSugar,
        totalProtein,
        totalSodium,
        totalFiber,
        dailyCaloriesPercent,
        dailyFatPercent,
        dailySugarPercent,
        fatPercent,
        carbPercent,
        proteinPercent,
      });
    } catch (err) {
      setError("Erreur Calcul: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    calculateResult();
  }, [donutType, size, quantity, customWeight, useCustomWeight, dailyCalories]);

  const handleClear = () => {
    setDonutType("glazed");
    setSize("medium");
    setQuantity(1);
    setCustomWeight("");
    setUseCustomWeight(false);
    setDailyCalories(2000);
    setResult(null);
    setError("");
  };

  const renderInputFields = () => (
    <div className="row">
      <div className="col-sm-12 col-md-6">
        <label >Type de Beignet</label>
        <select
          className="form-select"
          value={donutType}
          onChange={(e) => setDonutType(e.target.value)}
        >
          {Object.entries(DONUT_TYPES).map(([key, donut]) => (
            <option key={key} value={key}>{donut.name}</option>
          ))}
        </select>
      </div>
      <div className="col-sm-12 col-md-3">
        <label >Taille</label>
        <select
          className="form-select"
          value={size}
          onChange={(e) => setSize(e.target.value)}
        >
          {Object.entries(SIZE_MULTIPLIERS).map(([key, sizeOpt]) => (
            <option key={key} value={key}>{sizeOpt.name}</option>
          ))}
        </select>
      </div>
      <div className="col-sm-12 col-md-3">
        <label >Quantité</label>
        <input
          type="number"
          className="form-control"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          min="1"
          placeholder="Nombre"
        />
      </div>
    </div>
  );

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice Beignets</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                La <strong>calculatrice beignets</strong> vous aide à calculer les calories, graisses, sucres et autres nutriments dans différents types de beignets. Que vous fassiez un glaçage simple ou rempli, cette calculatrice vous donne les informations nutritionnelles exactes pour vous aider à suivre votre régime alimentaire  sans culpabilité.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Données Nutritionnelles Beignets</h2>
              </div>
              {renderInputFields()}
              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>
                  Effacer Les Données
                </button>
              </div>
              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Résultats Nutritionnels :</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nutriment</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Valeur</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Calories (Total)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#1a5f3f" }}>{result.totalCalories.toFixed(0)} kcal</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Graisses Totales</td>
                          <td style={{ textAlign: "center" }}>{result.totalFat.toFixed(1)} g</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Sucre</td>
                          <td style={{ textAlign: "center" }}>{result.totalSugar.toFixed(1)} g</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Glucides</td>
                          <td style={{ textAlign: "center" }}>{result.totalCarbs.toFixed(1)} g</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Qu'est-ce que la Calculatrice de Beignets ?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Calculatrice de Beignets</strong> est un outil gratuit et précis qui vous aide à{" "}
                  <strong>calculer les calories</strong>, les graisses, les sucres et les glucides dans les beignets selon leur type et leur taille. Les valeurs nutritionnelles varient considérablement selon le type de beignet, il est donc essentiel pour ceux qui comptent leurs calories ou qui suivent un régime alimentaire spécifique.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Comment utiliser la calculatrice :
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Choisissez le type de beignet dans la liste (glaçage, chocolat, crème...)</li>
                  <li>Sélectionnez la taille : très petit, moyen ou grand</li>
                  <li>Entrez le nombre de beignets que vous prévoyez de manger</li>
                  <li>Entrez vos besoins caloriques quotidiens pour connaître le pourcentage</li>
                  <li>Vous pouvez également entrer le poids des beignets manuellement si vous le connaissez avec précision</li>
                  <li>Les résultats s'affichent automatiquement sans avoir besoin de cliquer sur un bouton</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 Remarque :</strong> Les valeurs nutritionnelles utilisées sont basées sur des moyennes documentées des grandes marques et des bases de données alimentaires. Les valeurs réelles peuvent varier selon la recette et le lieu d'achat.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Utilisations de la Calculatrice de Beignets</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Pour la gestion du poids et la nutrition :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Suivi des calories :</strong> Pour ceux qui comptent leurs calories quotidiennes dans le contexte d'un régime
                  </li>
                  <li>
                    <strong>Surveillance du sucre :</strong> Très utile pour les diabétiques et ceux atteints de sensibilité au glucose
                  </li>
                  <li>
                    <strong>Équilibre nutritionnel :</strong> Vous aide à savoir combien vous devez compenser par d'autres repas
                  </li>
                  <li>
                    <strong>Réduction des graisses :</strong> Révèle la différence entre les types de beignets pour choisir le plus léger
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Dans les boulangeries et restaurants :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Étiquettes nutritionnelles :</strong> Préparation de données nutritionnelles pour les produits
                  </li>
                  <li>
                    <strong>Marketing santé :</strong> Mise en avant des options moins caloriques pour les clients
                  </li>
                  <li>
                    <strong>Comparaison :</strong> Comparaison de différents produits selon leur valeur nutritionnelle
                  </li>
                  <li>
                    <strong>Développement :</strong> Aide au développement de recettes plus légères et saines
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Dans la vie quotidienne :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Partage familial :</strong> Savoir combien vos enfants consomment de calories pendant une fête
                  </li>
                  <li>
                    <strong>Occasions spéciales :</strong> Planification des quantités de beignets pour les fêtes
                  </li>
                  <li>
                    <strong>Curiosité nutritionnelle :</strong> Comprendre clairement ce que vous mangez
                  </li>
                  <li>
                    <strong>Choix intelligent :</strong> Comparaison entre différents types avant d'acheter
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Calories dans les types de beignets courants</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Les beignets diffèrent considérablement dans leur composition nutritionnelle selon le type. Les beignets fourrés à la crème ou gelée contiennent généralement beaucoup plus de calories que les beignets simples, car le remplissage ajoute du poids, des graisses et des sucres supplémentaires. Voici une comparaison rapide :
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>Type</th>
                      <th style={{ textAlign: "center" }}>Poids approximatif (g)</th>
                      <th style={{ textAlign: "center" }}>Calories (kcal)</th>
                      <th style={{ textAlign: "center" }}>Graisses (g)</th>
                      <th style={{ textAlign: "center" }}>Sucre (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet nature</td>
                      <td style={{ textAlign: "center" }}>55</td>
                      <td style={{ textAlign: "center" }}>193</td>
                      <td style={{ textAlign: "center" }}>8,3</td>
                      <td style={{ textAlign: "center" }}>6,6</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet glacé</td>
                      <td style={{ textAlign: "center" }}>60</td>
                      <td style={{ textAlign: "center" }}>228</td>
                      <td style={{ textAlign: "center" }}>10,2</td>
                      <td style={{ textAlign: "center" }}>13,2</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet sucré</td>
                      <td style={{ textAlign: "center" }}>60</td>
                      <td style={{ textAlign: "center" }}>234</td>
                      <td style={{ textAlign: "center" }}>10,2</td>
                      <td style={{ textAlign: "center" }}>12</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet à la crème</td>
                      <td style={{ textAlign: "center" }}>85</td>
                      <td style={{ textAlign: "center" }}>357</td>
                      <td style={{ textAlign: "center" }}>18,7</td>
                      <td style={{ textAlign: "center" }}>21,3</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet à la gelée</td>
                      <td style={{ textAlign: "center" }}>85</td>
                      <td style={{ textAlign: "center" }}>349</td>
                      <td style={{ textAlign: "center" }}>15,3</td>
                      <td style={{ textAlign: "center" }}>25,5</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Beignet Old Fashioned</td>
                      <td style={{ textAlign: "center" }}>80</td>
                      <td style={{ textAlign: "center" }}>360</td>
                      <td style={{ textAlign: "center" }}>17,6</td>
                      <td style={{ textAlign: "center" }}>17,6</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Trou de beignet</td>
                      <td style={{ textAlign: "center" }}>25</td>
                      <td style={{ textAlign: "center" }}>93</td>
                      <td style={{ textAlign: "center" }}>4</td>
                      <td style={{ textAlign: "center" }}>4,5</td>
                    </tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Conseil :</strong> Les trous de beignet sont un choix judicieux pour ceux qui veulent manger des beignets avec moins de calories. Leur petite taille signifie le même plaisir sans excès de calories.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Exemples pratiques de calcul des calories des beignets</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Exemple 1 : Beignet glacé de taille moyenne
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Vous avez mangé un beignet glacé et voulez savoir combien cela représente de votre besoin quotidien de 2000 calories.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong> Poids du beignet = 60 g<br />
                  Calories par 100 g = 380<br />
                  Calories pour le beignet = 60 × 380 / 100 = 228 calories<br />
                  Pourcentage du jour = 228 / 2000 × 100 = 11,4%
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Exemple 2 : 3 trous de beignet
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Vous avez mangé 3 trous de beignet avec votre café du matin. Combien de calories ?
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong> Poids du beignet = 25 g<br />
                  Calories par beignet = 25 × 370 / 100 ≈ 93 calories<br />
                  Total pour 3 = 93 × 3 = 279 calories
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Exemple 3 : Beignet à la crème de grande taille
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Beignet à la crème de grande taille - Combien de graisses et de sucres ?
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong> Poids du grand beignet = 85 × 1,35 ≈ 115 g<br />
                  Graisses = 115 × 22 / 100 ≈ 25,3 g<br />
                  Sucres = 115 × 25 / 100 ≈ 28,8 g (57 % de la limite quotidienne!)
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Conseils pour profiter intelligemment des beignets</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Conseils généraux :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Choisissez une taille plus petite :</strong> Les beignets petits offrent le même plaisir avec environ la moitié des calories
                  </li>
                  <li>
                    <strong>Le timing compte :</strong> Manger le matin après l'exercice est préférable à manger le soir
                  </li>
                  <li>
                    <strong>Ne le mangez pas seul :</strong> Ajouter de la protéine ralentit l'absorption du sucre
                  </li>
                  <li>
                    <strong>La modération est essentielle :</strong> Une ou deux fois par semaine est acceptable dans un régime équilibré
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Erreurs courantes à éviter :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Penser que les petits beignets « ne comptent pas » - chaque calorie compte</li>
                  <li>Oublier les calories de la boisson accompagnante</li>
                  <li>Comparer les beignets avec d'autres aliments sains - les deux sont des sucreries</li>
                  <li>Manger plusieurs parce que « c'est petit » - la quantité double les calories</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Faits intéressants sur les beignets</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Origine :</strong> L'apparition du beignet est attribuée aux immigrants hollandais en Amérique au 19ème siècle
                  </li>
                  <li>
                    <strong>Le trou :</strong> Selon la légende, le capitaine Hanson Gregory a inventé le trou en 1847 pour assurer une cuisson uniforme de la pâte
                  </li>
                  <li>
                    <strong>Jour des beignets :</strong> Le premier vendredi de juin est la « Journée nationale des beignets » aux États-Unis
                  </li>
                  <li>
                    <strong>Chiffres énormes :</strong> Plus de 10 milliards de beignets sont consommés chaque année juste aux États-Unis
                  </li>
                  <li>
                    <strong>Saveurs régionales :</strong> Les boulangeries arabes proposent désormais des beignets avec des saveurs locales comme les dattes, la crème et le miel
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Questions fréquemment posées</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  Combien de calories dans un beignet glacé ?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Un beignet glacé de taille moyenne contient environ 220-240 calories. Ce nombre dépend du poids réel, car les beignets moyens pèsent entre 55 et 65 grammes. Par exemple, un beignet Krispy Kreme glacé contient 190 calories tandis qu'un produit Dunkin atteint 240 calories.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  Can you eat donuts on a diet?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Oui, vous pouvez le manger avec modération dans n'importe quel régime équilibré. L'important est de compter ses calories dans votre budget quotidien. Choisissez la taille la plus petite ou les trous de beignet pour réduire les calories, et évitez les variétés fourrées à la crème si vous surveillez les graisses. Une fois par semaine ne ruinera pas votre régime.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  Comment calculer les calories d'un beignet fait maison ?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Utilisez l'option « Entrez le poids manuellement » et entrez le poids de votre beignet en grammes. La calculatrice vous donnera une bonne estimation basée sur le type le plus proche de votre recette. Pour un calcul plus précis, calculez les calories de chaque ingrédient et divisez par le nombre de beignets produits.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  Les trous de beignet sont-ils plus légers en calories ?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Absolument, car ils sont beaucoup plus petits et pèsent 20-30 grammes contre 55-85 grammes pour les beignets réguliers. Cependant, attention : si vous mangez 5 ou 6 trous de beignet, vous pourriez dépasser les calories d'un beignet normal. La sensibilisation à la quantité est la vraie clé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DonutCalculator;
